import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import type Handsontable from 'handsontable';
import Papa from 'papaparse';

interface ColumnSettings {
  title: string;
  data: string;
  width: number;
  type: string;
}

interface SpreadsheetContextType {
  hotInstance: Handsontable | null;
  setHotInstance: (instance: Handsontable | null) => void;
  updateCell: (column: string, row: number, value: string | number) => boolean;
  addRow: () => void;
  addColumn: () => void;
  getSpreadsheetData: () => unknown[][];
  getCsvData: () => string;
}

const SpreadsheetContext = createContext<SpreadsheetContextType | null>(null);

export const SpreadsheetProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [hotInstance, setHotInstance] = useState<Handsontable | null>(null);

  useEffect(() => {
    return () => {
      if (hotInstance) {
        setHotInstance(null);
      }
    };
  }, [hotInstance]);

  const updateCell = useCallback(
    (column: string, row: number, value: string | number): boolean => {
      if (!hotInstance) {
        console.log('No hotInstance found');
        return false;
      }

      try {
        const settings = hotInstance.getSettings();
        let colIndex = -1;

        if (settings && settings.columns) {
          const columns = settings.columns as ColumnSettings[];
          colIndex = columns.findIndex((col) => col.data === column);
        } else {
          // If no explicit columns, try to parse column as letter (A, B, C, etc.) or number
          if (column.match(/^[A-Z]$/)) {
            colIndex = column.charCodeAt(0) - 65;
          } else if (!isNaN(parseInt(column))) {
            // If it's a number, use it directly (0-based)
            colIndex = parseInt(column);
          }
        }

        if (colIndex === -1) {
          console.log(`Column ${column} not found`);
          return false;
        }

        const data = hotInstance.getData();
        if (data.length > 0 && colIndex >= data[0].length) {
          console.log(`Column index ${colIndex} is out of range`);
          return false;
        }

        const rowIndex = row - 1;
        if (rowIndex < 0) {
          console.log('Invalid row number');
          return false;
        }

        hotInstance.setDataAtCell(rowIndex, colIndex, value);
        return true;
      } catch (error) {
        console.error('Error updating cell:', error);
        return false;
      }
    },
    [hotInstance],
  );

  const addRow = useCallback(() => {
    if (!hotInstance) {
      console.log('No hotInstance found');
      return;
    }

    try {
      const currentData = hotInstance.getData();
      const colCount = currentData.length > 0 ? currentData[0].length : 1;

      const newRow = new Array(colCount).fill('');
      const newData = [...currentData, newRow];

      hotInstance.loadData(newData);

      // Use requestAnimationFrame instead of setTimeout
      requestAnimationFrame(() => {
        if (hotInstance) {
          hotInstance.scrollViewportTo(currentData.length);
        }
      });
    } catch (error) {
      console.error('Error adding row:', error);
    }
  }, [hotInstance]);

  const addColumn = useCallback(() => {
    if (!hotInstance) {
      console.log('No hotInstance found');
      return;
    }

    try {
      const currentData = hotInstance.getData();
      const newColIndex = currentData.length > 0 ? currentData[0].length : 0;

      const newData = currentData.map((row) => {
        const newRow = [...row];
        newRow[newColIndex] = '';
        return newRow;
      });

      // If no data exists, create a single row with one column
      if (newData.length === 0) {
        newData.push(['']);
      }

      hotInstance.loadData(newData);

      // Use requestAnimationFrame instead of setTimeout
      requestAnimationFrame(() => {
        if (hotInstance) {
          hotInstance.selectCell(0, newColIndex);
          hotInstance.scrollViewportTo(0, newColIndex);
        }
      });
    } catch (error) {
      console.error('Error adding column:', error);
    }
  }, [hotInstance]);

  const getSpreadsheetData = useCallback(() => {
    if (!hotInstance) return [];
    try {
      return hotInstance.getData();
    } catch (error) {
      console.error('Error getting spreadsheet data:', error);
      return [];
    }
  }, [hotInstance]);

  const getCsvData = useCallback(() => {
    const data = getSpreadsheetData();
    return Papa.unparse(data);
  }, [getSpreadsheetData]);

  return (
    <SpreadsheetContext.Provider
      value={{
        hotInstance,
        setHotInstance,
        updateCell,
        addRow,
        addColumn,
        getSpreadsheetData,
        getCsvData,
      }}
    >
      {children}
    </SpreadsheetContext.Provider>
  );
};

export const useSpreadsheet = () => {
  const context = useContext(SpreadsheetContext);
  if (!context) {
    throw new Error('useSpreadsheet must be used within a SpreadsheetProvider');
  }
  return context;
};
